/* ============================================================
   Cloud Store · Shop-wide reviews marquee
   Fetches /api/v1/widgets/featured_carousel (Judge.me public),
   parses the HTML and renders a horizontal infinite marquee with
   product image + link, distinct from the per-product carousel.
   ============================================================ */
(function () {
  if (window.__csShopReviewsInit) return;
  window.__csShopReviewsInit = true;

  const SEL = {
    section: '[data-cs-shopr]',
    viewport: '[data-cs-shopr-viewport]',
  };

  const COLORS = ['#6B4FB8', '#5E2F4D', '#5C7A55', '#C99A3D', '#A78BD8', '#7A5DC4', '#A8B89A'];

  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  function initialFromName(name) {
    const trimmed = (name || '').trim();
    if (!trimmed) return 'C';
    const first = trimmed.charAt(0).toUpperCase();
    return /[A-Z0-9]/.test(first) ? first : 'C';
  }

  function colorFromName(name) {
    let hash = 0;
    const s = name || 'anon';
    for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash);
    return COLORS[Math.abs(hash) % COLORS.length];
  }

  function renderStars(n, max = 5) {
    let s = '';
    for (let i = 1; i <= max; i++) {
      s += `<span class="cs-shopr-star${i <= n ? ' is-on' : ''}" aria-hidden="true">★</span>`;
    }
    return s;
  }

  async function fetchCarouselHTML({ token, shopDomain }) {
    const url = `https://judge.me/api/v1/widgets/featured_carousel`
      + `?api_token=${encodeURIComponent(token)}`
      + `&shop_domain=${encodeURIComponent(shopDomain)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Judge.me featured_carousel ${res.status}`);
    const json = await res.json();
    return json.featured_carousel || '';
  }

  function parseShopReviews(htmlString) {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const reviews = [];
    doc.querySelectorAll('.jdgm-carousel-item').forEach((el) => {
      const body = (el.querySelector('.jdgm-carousel-item__review-body') || {}).textContent || '';
      const reviewer = (el.querySelector('.jdgm-carousel-item__reviewer-name') || {}).textContent || 'Anonyme';
      const productLinkEl = el.querySelector('.jdgm-carousel-item__product');
      const productImgEl = el.querySelector('.jdgm-carousel-item__product-image');
      const productTitleEl = el.querySelector('.jdgm-carousel-item__product-title');
      const ratingStars = el.querySelectorAll('.jdgm-carousel-item__review-rating .jdgm-star.jdgm--on').length;
      const productHref = productLinkEl ? productLinkEl.getAttribute('href') : null;
      const productImg = productImgEl ? (productImgEl.getAttribute('data-src-retina') || productImgEl.getAttribute('data-src') || productImgEl.getAttribute('src')) : null;
      const productTitle = productTitleEl ? productTitleEl.textContent.trim() : '';

      const txt = body.trim();
      if (!txt) return;
      reviews.push({
        body: txt,
        reviewer: reviewer.trim(),
        productHref: productHref ? productHref.split('#')[0] : null,
        productImg,
        productTitle,
        rating: ratingStars || 5,
      });
    });
    return reviews;
  }

  function renderCard(r) {
    const initial = initialFromName(r.reviewer);
    const color = colorFromName(r.reviewer);
    const linkOpen = r.productHref ? `<a class="cs-shopr-card__product" href="${escapeHtml(r.productHref)}">` : `<div class="cs-shopr-card__product cs-shopr-card__product--inert">`;
    const linkClose = r.productHref ? `</a>` : `</div>`;
    const imgHtml = r.productImg
      ? `<img class="cs-shopr-card__img" src="${escapeHtml(r.productImg)}" alt="${escapeHtml(r.productTitle)}" loading="lazy" />`
      : `<div class="cs-shopr-card__img cs-shopr-card__img--placeholder" aria-hidden="true">🌿</div>`;
    return `
      <article class="cs-shopr-card">
        ${linkOpen}
          ${imgHtml}
          <div class="cs-shopr-card__product-meta">
            <span class="cs-shopr-card__product-eyebrow">Produit</span>
            <span class="cs-shopr-card__product-title">${escapeHtml(r.productTitle)}</span>
          </div>
        ${linkClose}
        <div class="cs-shopr-card__stars">${renderStars(r.rating || 5)}</div>
        <p class="cs-shopr-card__body">${escapeHtml(r.body)}</p>
        <div class="cs-shopr-card__reviewer">
          <span class="cs-shopr-card__avatar" style="background:${color}">${escapeHtml(initial)}</span>
          <span class="cs-shopr-card__name">${escapeHtml(r.reviewer)}</span>
        </div>
      </article>
    `;
  }

  function renderMarquee(viewport, reviews) {
    if (!reviews.length) {
      viewport.innerHTML = '<div class="cs-shopr__empty">Aucun avis boutique pour l’instant.</div>';
      return;
    }
    // Duplicate twice for seamless loop
    const cards = reviews.map(renderCard).join('');
    // Slow scroll : ~6s of reading per card window, clamp 90s..300s
    const dur = Math.max(90, Math.min(300, reviews.length * 12));
    viewport.innerHTML = `
      <div class="cs-shopr__rail" style="--cs-shopr-dur:${dur}s">
        <div class="cs-shopr__track" data-cs-shopr-track>
          ${cards}${cards}
        </div>
      </div>
    `;
    // Pause/resume — desktop uses real hover, mobile uses touch (iOS never fires mouseleave after tap)
    const track = viewport.querySelector('[data-cs-shopr-track]');
    if (!track) return;

    const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    if (!isTouch) {
      // Desktop : pause while hovering
      viewport.addEventListener('mouseenter', () => track.style.animationPlayState = 'paused');
      viewport.addEventListener('mouseleave', () => track.style.animationPlayState = 'running');
    } else {
      // Mobile : pause briefly on tap-and-hold, always auto-resume so it never gets stuck
      let resumeTimer;
      const pause = () => {
        track.style.animationPlayState = 'paused';
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(() => {
          track.style.animationPlayState = 'running';
        }, 3000);
      };
      const resumeNow = () => {
        clearTimeout(resumeTimer);
        track.style.animationPlayState = 'running';
      };
      viewport.addEventListener('touchstart', pause, { passive: true });
      viewport.addEventListener('touchcancel', resumeNow, { passive: true });
      // Also resume when user scrolls the page away — fixes "stuck paused" if mouseenter fires from a synthetic event
      window.addEventListener('scroll', resumeNow, { passive: true });
    }
  }

  async function init() {
    const sections = document.querySelectorAll(SEL.section);
    for (const section of sections) {
      const token = section.getAttribute('data-cs-jdgm-token');
      const shopDomain = section.getAttribute('data-cs-shop-domain');
      const viewport = section.querySelector(SEL.viewport);
      if (!token || !shopDomain || !viewport) continue;

      try {
        const html = await fetchCarouselHTML({ token, shopDomain });
        const reviews = parseShopReviews(html);
        renderMarquee(viewport, reviews);
      } catch (err) {
        console.error('[cs-shop-reviews] fetch failed', err);
        viewport.innerHTML = '<div class="cs-shopr__empty">Avis boutique indisponibles pour le moment.</div>';
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
